import { useId, useState } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';

import { useHeroFeatureFlags } from '../useHeroFeatureFlags';

type HeroTypingPlaceholderHookResult = ModelHookResult<
	{
		value: string;
		/** Unset while the overlay is on, so a native placeholder doesn't sit under the typed one. */
		placeholder: string | undefined;
		showOverlay: boolean;
		/**
		 * Passed to `HeroTypingOverlay`, and the field's `aria-describedby` target while the overlay is
		 * on: the overlay is aria-hidden, so this carries the hint.
		 */
		descriptionId: string;
	},
	{
		onValueChange: (value: string) => void;
	}
>;

/**
 * Decides whether a hero field shows the typed placeholder overlay or a native one, and tracks the
 * field's value so the overlay can drop away once there is input.
 *
 * Returns a flag rather than the overlay itself: the overlay lives in `shared/components`, which
 * imports this hook's barrel, so rendering it here would close an import cycle.
 */
export function useHeroTypingPlaceholder(): HeroTypingPlaceholderHookResult {
	const { translate } = useI18n();
	const {
		data: { flags },
	} = useHeroFeatureFlags();
	const [value, setValue] = useState('');
	const descriptionId = useId();

	const typingPlaceholder = flags.typingPlaceholderAnimation ?? false;
	const staticPlaceholder = translate('corgi.homepage.hero_section.input_prompt_placeholder');
	// Like a native placeholder, the overlay and its description only show while the field is empty.
	const showOverlay = typingPlaceholder && !value;

	return {
		data: {
			value,
			placeholder: typingPlaceholder ? undefined : staticPlaceholder,
			showOverlay,
			descriptionId,
		},
		actions: { onValueChange: setValue },
	};
}
